import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon, ZapIcon, LockIcon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { tbToChartData } from '../utils/tbChartHelper';
import { thingsboardService } from '../services/thingsboard';
import { formatEnergyUsage } from '../utils/energyUtils';
import { useTranslation } from 'react-i18next';

import {
  TimeRange, SensorDetailSidebarProps,
  TimeRangeTabs, ChartSection, PasswordToast, ToggleSwitch, SensorHeader
} from './SensorDetailShared';

export type SEMMode = 'occupancy' | 'always' | 'ai' | 'normal';

export const SEM_MODES: {
  id: SEMMode;
  label: string;
  value: number;
}[] = [
  { id: 'occupancy', label: '재실', value: 1 },
  { id: 'always', label: '상시', value: 2 },
  { id: 'ai', label: 'AI', value: 3 },
  { id: 'normal', label: '일반', value: 4 }
];

export function SEMDetail({ sensor, onBack, onClose, onUpdateSensor, onShowToast }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const isOnline = Boolean(sensor.active);
  const telemetryKeys = useMemo(() => ['power_instant', 'temperature', 'plug_on', 'plug_mode', 'active'], []);
  const { data: realtimeData } = useTelemetry(deviceId, telemetryKeys);

  const [envTimeRange, setEnvTimeRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [powerTimeRange, setPowerTimeRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');

  const powerKey = powerTimeRange === 'realtime' ? 'power_instant' :
                   powerTimeRange === 'daily' ? 'hourly_power' :
                   powerTimeRange === 'weekly' ? 'daily_power' : 'daily_power';

  const { data: histPower } = useTelemetryHistory(deviceId, [powerKey], powerTimeRange);
  const { data: histTemp } = useTelemetryHistory(deviceId, ['temperature'], envTimeRange);

  const finalPowerData = useMemo(() => {
    return powerTimeRange === 'realtime'
      ? tbToChartData(realtimeData, 'power_instant', 'realtime', [])
      : tbToChartData(histPower, powerKey, powerTimeRange, [], 0.001);
  }, [powerTimeRange, realtimeData, histPower, powerKey]);

  const finalTempData = useMemo(() => {
    return envTimeRange === 'realtime'
      ? tbToChartData(realtimeData, 'temperature', 'realtime', [])
      : tbToChartData(histTemp, 'temperature', envTimeRange, []);
  }, [envTimeRange, realtimeData, histTemp]);

  const [mode, setMode] = useState<SEMMode>('occupancy');
  const [isOn, setIsOn] = useState(true);

  useEffect(() => {
    const switchState = realtimeData?.['plug_on'];
    const plugModeState = realtimeData?.['plug_mode'];
    if (switchState && switchState.length > 0) {
      const val = switchState[0].value;
      setIsOn(String(val) === 'true' || String(val) === 'on');
    }
    if (plugModeState && plugModeState.length > 0) {
      const val = Number(plugModeState[0].value);
      const matchedMode = SEM_MODES.find((m) => Math.floor(m.value) === Math.floor(val));
      if (matchedMode) {
        setMode(matchedMode.id);
      }
    }
  }, [realtimeData]);

  const handleToggleOutlet = async () => {
    if (!isOnline) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    const newValue = !isOn;
    setIsOn(newValue); 
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue ? "on" : "off" });
    } catch (error) {
      console.error('Failed to send RPC command for SEM plug switch:', error);
      setIsOn(!newValue); 
    }
    setTimeout(() => {
      try {
        thingsboardService.sendOneWayRpc(deviceId, 'set_plug', { value: newValue ? "on" : "off" });
      } catch (error) {
        console.error('Failed to send RPC command for SEM plug switch:', error);
        setIsOn(!newValue); 
      }
    }, 100);
  };

  const [password, setPassword] = useState('0000');
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingMode, setPendingMode] = useState<SEMMode | null>(null);

  const applyModeChange = async (targetModeId: SEMMode) => {
    setMode(targetModeId);
    try {
      const targetMode = SEM_MODES.find((m) => m.id === targetModeId);
      if (targetMode) {
        await thingsboardService.sendOneWayRpc(deviceId, 'set_seo_mode', { value: targetMode.value });
      }
    } catch (error) {
      console.error('Failed to send RPC command for SEM plug mode:', error);
    }
  };

  const handleToggleMode = (newMode: SEMMode) => {
    if (!isOnline) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    if (newMode === mode) return;

    // Both entering and exiting 'always' mode require password verification
    if (newMode === 'always' || mode === 'always') {
      setPendingMode(newMode);
      setShowPasswordVerify(true);
      return;
    }

    applyModeChange(newMode);
  };

  const handlePasswordVerified = (success: boolean) => {
    setShowPasswordVerify(false);
    if (success && pendingMode !== null) {
      applyModeChange(pendingMode);
    }
    setPendingMode(null);
  };

  const handlePasswordChanged = (newPw: string) => {
    setPassword(newPw);
    setShowPasswordChange(false);
  };

  const currentTemp = finalTempData.length > 0 && finalTempData[finalTempData.length - 1].value !== undefined
    ? Number(finalTempData[finalTempData.length - 1].value).toFixed(1)
    : (realtimeData?.['temperature']?.[0]?.value ? Number(realtimeData['temperature'][0].value).toFixed(1) : '--');

  const avgTemp = finalTempData.length > 0
    ? (finalTempData.reduce((sum: number, item: any) => sum + (Number(item.value) || 0), 0) / finalTempData.length).toFixed(1)
    : '--';

  return (
    <>
      <div className="w-full flex flex-col gap-2 p-3 border-l border-white/20 bg-[#0a0f1a]/30 h-full overflow-hidden">
        <SensorHeader
          sensor={sensor}
          subtitle="Smart Edge Meter"
          onBack={onBack}
          onClose={onClose}
          onUpdateSensor={onUpdateSensor}
          onShowToast={onShowToast}
        />

        {/* Mode Selection - 4 Buttons + Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-2 flex-shrink-0 flex items-center justify-between gap-2"
        >
          {/* 4 Mode Buttons Group */}
          <div className={`grid grid-cols-4 gap-1 bg-black/40 p-1 rounded-xl border border-white/10 flex-1 transition-opacity ${
            !isOnline ? 'opacity-50' : ''
          }`}>
            {SEM_MODES.map((m) => {
              const isSelected = mode === m.id;
              const isAlways = m.id === 'always';
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleToggleMode(m.id)}
                  className={`py-1.5 px-1 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-0.5 ${
                    !isOnline
                      ? isSelected
                        ? isAlways
                          ? 'bg-amber-500/50 text-slate-900 cursor-not-allowed'
                          : 'bg-cyan-500/50 text-slate-900 cursor-not-allowed'
                        : 'text-slate-500 hover:text-slate-400 cursor-not-allowed'
                      : isSelected
                      ? isAlways
                        ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20 active:scale-95'
                        : 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20 active:scale-95'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 active:scale-95'
                  }`}
                >
                  {isAlways && <LockIcon className="w-3 h-3" />}
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right side: Toggle + Password button */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <AnimatePresence>
              {mode === 'always' && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => {
                    if (!isOnline) {
                      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
                      return;
                    }
                    setShowPasswordChange(true);
                  }}
                  className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 transition-all active:scale-95 flex-shrink-0"
                  title={t('sensor.set_password')}
                >
                  <LockIcon className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>

            {mode === 'normal' && (
              <ToggleSwitch isOn={isOn} onToggle={handleToggleOutlet} disabled={!isOnline} />
            )}
          </div>
        </motion.div>

        <ChartSection
          icon={ZapIcon}
          iconColor="text-violet-400"
          title={t('sensor.energy_usage')}
          data={finalPowerData}
          timeRange={powerTimeRange}
          onTimeRangeChange={setPowerTimeRange}
          disabledRanges={!sensor.active ? ['realtime'] : []}
          chartType="area"
          color="#a78bfa"
          gradientId="semPower"
          className="flex-1 min-h-0"
          unit={powerTimeRange === 'realtime' ? 'W' : 'kWh'}
          stats={[
            {
              label: powerTimeRange === 'realtime' ? t('sensor.current_power_usage') : 
                     powerTimeRange === 'daily' ? t('sensor.today_total_usage') :
                     powerTimeRange === 'weekly' ? t('sensor.this_week_total_usage') : t('sensor.this_month_total_usage'),
              value: powerTimeRange === 'realtime' 
                ? `${Number(realtimeData?.['power_instant']?.[0]?.value || 0).toFixed(1)} W`
                : formatEnergyUsage(finalPowerData.length > 0 ? finalPowerData.reduce((sum, item) => sum + (Number(item.value) || 0), 0) : 0).fullText,
              color: 'text-violet-400'
            }
          ]}
        />

        {/* Temperature Only Card (No Humidity) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel rounded-xl p-3 flex-1 min-h-0 flex flex-col"
        >
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
              <span className="text-[0.9rem] font-bold text-slate-300">
                {t('sensor.temperature', '실내 온도')}
              </span>
            </div>
            <TimeRangeTabs value={envTimeRange} onChange={setEnvTimeRange} disabledRanges={!sensor.active ? ['realtime'] : []} />
          </div>

          <div className="flex items-center gap-3 mb-2 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
              <span className="text-[0.8rem] text-slate-500 uppercase font-bold">{t('sensor.temperature')}</span>
              <span className="text-base font-roboto font-bold text-rose-400">
                {currentTemp}°C
              </span>
            </div>
          </div>

          <div className="flex-1 min-h-0 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={finalTempData}>
                <defs>
                  <linearGradient id="semTempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" hide />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0a0f1a',
                    borderColor: '#334155',
                    fontSize: '0.7rem',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`${Number(value).toFixed(1)}°C`, t('sensor.temperature')]}
                />
                <Area type="monotone" dataKey="value" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#semTempGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 rounded-full bg-rose-400" />
              <span className="text-slate-500 text-[0.7rem]">{t('sensor.average', '평균')}</span>
              <span className="font-mono text-white">
                {avgTemp}°C
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showPasswordVerify && (
          <PasswordToast
            type="verify"
            storedPassword={password}
            onClose={() => {
              setShowPasswordVerify(false);
              setPendingMode(null);
            }}
            onVerify={handlePasswordVerified}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPasswordChange && (
          <PasswordToast
            type="change"
            storedPassword={password}
            onClose={() => setShowPasswordChange(false)}
            onChangeComplete={handlePasswordChanged}
          />
        )}
      </AnimatePresence>
    </>
  );
}
